// CTW-DURMAH-STUDENT-MANUAL-01: Workspace + Lecture Intelligence sections.

import type { ManualSection } from "../types";

export const workspaceSections: ManualSection[] = [
  {
    slug: "dashboard-tour",
    title: "Your dashboard, explained",
    summary:
      "The dashboard is a priority screen, not a menu. It exists to answer one question — what should I do next? — and then get out of the way.",
    category: "your-workspace",
    audience: "public",
    order: 1,
    readTime: "4 min read",
    tags: ["Workspace", "Daily use"],
    keywords: [
      "dashboard",
      "home screen",
      "what should i do next",
      "focus",
      "priority",
      "today",
      "main screen",
      "landing page after login",
    ],
    blocks: [
      {
        kind: "heading",
        id: "focus-stage",
        text: "The Focus Stage",
      },
      {
        kind: "para",
        text: "The large panel at the top is the most important thing on the screen. It shows the single item Durmah believes matters most right now — usually the nearest real academic commitment — with a button that takes you straight into the work.",
      },
      {
        kind: "bullets",
        intro: "Alongside it you get:",
        items: [
          "Where the item came from, which module it belongs to, and when it is due.",
          "A tappable countdown you can switch on or off.",
          "View in YAAG, to see the item in the context of your whole year.",
          "Mark submitted, for assignments.",
          "Why this? — an explanation of why Durmah picked this item, with its reasoning, the strategy behind it, and the source. Durmah shows its working rather than asking you to trust it.",
        ],
      },
      {
        kind: "callout",
        tone: "boundary",
        title: "It will not invent urgency",
        text: "If there is genuinely nothing pressing, the panel says so and suggests adding an assignment or importing your timetable. Old work gets a quiet chip, never a red 'overdue' badge designed to make you anxious. A dashboard that manufactures pressure is a dashboard you stop trusting.",
      },
      {
        kind: "heading",
        id: "study-workspace",
        text: "Your four core spaces",
      },
      {
        kind: "para",
        text: "Below the focus panel sit the four cards that form the spine of the product. They are always in the same order, so you can find them without reading.",
      },
      {
        kind: "table",
        headers: ["Card", "What it is for"],
        rows: [
          [
            "Year at a Glance",
            "Teaching weeks, deadlines, exams and calendar commitments in context.",
          ],
          [
            "My Lectures",
            "Transcripts, summaries, lecturer emphasis and linked concepts.",
          ],
          [
            "Assignments",
            "Plan, research, draft and review without outsourcing your judgement.",
          ],
          [
            "Mark My Paper",
            "Structured review of your own draft, while you stay the author.",
          ],
        ],
      },
      {
        kind: "heading",
        id: "everything-else",
        text: "The rest of the page",
      },
      {
        kind: "bullets",
        items: [
          "Research & Academic Tools — Research Hub, SyllabusShield™ coverage, the Lexicon, and Exam Prep.",
          "Support & Community — the Community Hub, the Wellbeing Coach, and live legal news.",
          "Your next useful steps — grounded, specific recommendations. If Durmah has nothing genuinely useful to suggest, this panel shows nothing rather than filler.",
          "Sticky notes and today's tasks.",
          "An occasional suggested resource or legal news item, at most one per visit and on a cooldown so it never becomes noise.",
        ],
      },
    ],
    related: ["finding-your-way", "year-at-a-glance", "floating-tools"],
  },

  {
    slug: "finding-your-way",
    title: "Finding your way around",
    summary:
      "What sits in each part of the navigation once you are signed in, and where to look for the thing you need.",
    category: "your-workspace",
    audience: "public",
    order: 2,
    readTime: "2 min read",
    tags: ["Workspace", "Navigation"],
    keywords: [
      "navigation",
      "menu",
      "where is",
      "how do i find",
      "cant find",
      "where do i go",
      "site map",
    ],
    blocks: [
      {
        kind: "table",
        intro: "The signed-in menu groups everything into a few predictable places.",
        headers: ["Menu", "What is in it"],
        rows: [
          ["Dashboard", "Your priority screen."],
          [
            "My Studies",
            "My Modules, Year at a Glance, My Lectures, My Assignments, Practice Desk, Mark My Paper, Exam Prep, Research Hub and the LNAT mentor.",
          ],
          ["Legal Updates", "The live legal news feed."],
          ["My Community", "The Premier Lounge and your Community Hub."],
          ["My Tools", "Quiz Me and the Durmah Lexicon™."],
          ["Learning Guides", "The full public library of study guides."],
          ["Student Manual", "This manual."],
          [
            "Your account menu",
            "Profile, session and security, billing, and support.",
          ],
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "The fastest route is usually the keyboard",
        text: "Press Cmd+K (or Ctrl+K on Windows) anywhere in the app to open Lexicon search without losing your place. On this manual, the search box at the top of every page does the same job for how-to questions.",
      },
    ],
    related: ["dashboard-tour", "floating-tools", "durmah-lexicon"],
  },

  {
    slug: "floating-tools",
    title: "The tools that follow you around",
    summary:
      "Four things are available on every page once you are signed in — and one of them deliberately disappears while you are writing.",
    category: "your-workspace",
    audience: "public",
    order: 3,
    readTime: "2 min read",
    tags: ["Workspace", "Daily use"],
    keywords: [
      "floating button",
      "durmah button",
      "chat button",
      "bottom right",
      "quiz me button",
      "feedback button",
      "widget",
      "where did the chat button go",
      "cmd k",
      "keyboard shortcut",
    ],
    blocks: [
      {
        kind: "table",
        headers: ["Tool", "How to open it", "What it does"],
        rows: [
          [
            "Durmah",
            "The launcher at the bottom right",
            "Text chat and voice, aware of the page you are on.",
          ],
          [
            "Share Feedback",
            "The lightbulb button",
            "Sends straight to the product team. Use it liberally.",
          ],
          [
            "Lexicon search",
            "Cmd+K or Ctrl+K",
            "Look up a legal term from anywhere without losing your place.",
          ],
          [
            "Quiz Me",
            "From My Tools, or the dashboard card",
            "Start a quiz over a lecture, module, assignment or week.",
          ],
        ],
      },
      {
        kind: "callout",
        tone: "note",
        title: "Why the Durmah button vanishes sometimes",
        text: "On focused writing surfaces — inside the assignment workflow editor, on Mark My Paper, and on the writing hub — the launcher and the feedback button hide themselves. This is deliberate: when you are writing, nothing floats over your text. Chat is still available, embedded in the page where it belongs.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Pick up where you left off",
        text: "If you leave mid-task, a small pill appears near the top of the page offering to return you to the work page you last used. Handy when a seminar interrupts you halfway through a draft.",
      },
    ],
    related: ["talking-to-durmah", "durmah-lexicon", "getting-help"],
  },

  /* ---------------------------------------------------------------- */
  /* Lecture Intelligence                                              */
  /* ---------------------------------------------------------------- */

  {
    slug: "lecture-intelligence",
    title: "What Lecture Intelligence gives you",
    summary:
      "Give Durmah a lecture and it returns a structured study asset — summary, key points, vocabulary, the authorities the lecturer actually relied on, and a read on what they emphasised.",
    category: "lectures",
    audience: "public",
    order: 1,
    readTime: "4 min read",
    tags: ["Lectures", "Flagship"],
    keywords: [
      "lecture intelligence",
      "lecture notes",
      "lecture summary",
      "what do i get from a lecture",
      "lecture analysis",
      "exam signals",
      "lecturer emphasis",
    ],
    blocks: [
      {
        kind: "para",
        text: "Everything Lecture Intelligence produces is grounded in the transcript you supplied. Durmah does not add doctrine the lecturer did not mention, and it does not predict your exam paper.",
      },
      {
        kind: "table",
        intro: "Open a completed lecture and you will find:",
        headers: ["Output", "What it is"],
        rows: [
          ["Summary", "A concise account of what the lecture actually covered."],
          ["Key points", "The substantive points, pulled out and listed."],
          [
            "Discussion topics",
            "Prompts suitable for a seminar or a study group.",
          ],
          ["Exam prompts", "Practice questions grounded in the lecture."],
          [
            "Glossary",
            "Term-and-definition pairs, offered straight to your Durmah Lexicon™.",
          ],
          [
            "Authorities",
            "Cases, statutes, treaties, regulations and scholarship the lecturer relied on — each with its type, jurisdiction, the proposition it supports, and a confidence indicator.",
          ],
          ["Engagement hooks", "Angles that make the material stick."],
          [
            "Exam signals",
            "A read on lecturer emphasis: what they stressed, why it matters, likely exam angles, supporting quotes from the transcript, and practice prompts.",
          ],
        ],
      },
      {
        kind: "heading",
        id: "how-it-connects",
        text: "How it connects to everything else",
      },
      {
        kind: "bullets",
        items: [
          "Glossary terms flow into your personal Lexicon.",
          "Topics feed module coverage, so SyllabusShield™ can show you what you have not covered.",
          "Authorities become available to the Research Hub.",
          "Lecture content informs Mark My Paper when it reviews a draft for the same module.",
        ],
      },
      {
        kind: "callout",
        tone: "boundary",
        title: "Emphasis, not prediction",
        text: "Durmah highlights what a lecturer stressed so you can revise in proportion. It does not predict exam papers, and it will not generate work for you to submit as your own. Where the transcript does not support a reliable read, it says so instead of guessing.",
      },
    ],
    related: ["uploading-a-lecture", "asking-about-a-lecture", "my-modules"],
  },

  {
    slug: "uploading-a-lecture",
    title: "Adding a lecture",
    summary:
      "Two routes in: paste a transcript, which is fast and reliable, or upload audio and let Durmah transcribe it.",
    category: "lectures",
    audience: "public",
    order: 2,
    readTime: "4 min read",
    tags: ["Lectures", "How-to"],
    keywords: [
      "upload lecture",
      "add lecture",
      "panopto",
      "transcript",
      "captions",
      "audio",
      "mp3",
      "recording",
      "how do i add a lecture",
      "import lecture",
    ],
    blocks: [
      {
        kind: "heading",
        id: "transcript",
        text: "Route 1: paste a transcript (recommended)",
      },
      {
        kind: "steps",
        items: [
          "Select Add Lecture. The panel opens on the transcript tab.",
          "Give the lecture a title, pick the module, and add the lecturer and date.",
          "Optionally paste the Panopto link. This is a reference only — the transcript below it does the work.",
          "Either drop in a .txt transcript file, or paste the text into the box.",
          "Select Import Transcript.",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "The thirty-second route",
        text: "Open your lecture in Panopto and go to Captions. Download the transcript and save it as a .txt file. Upload that file. Analysis for a pasted transcript usually runs immediately.",
      },
      {
        kind: "heading",
        id: "audio",
        text: "Route 2: upload audio",
      },
      {
        kind: "steps",
        intro: "Use this when no transcript exists.",
        items: [
          "Select Add Lecture and switch to the audio tab.",
          "Fill in the title, module, lecturer and date as above.",
          "Choose your audio file. MP3, M4A, WAV and OGG are supported.",
          "Select Upload Audio.",
        ],
      },
      {
        kind: "para",
        text: "Durmah transcribes the audio and then analyses it. This runs in the background, so you can leave the page — the lecture moves through uploaded, queued, processing, transcribing, summarizing and finally ready. Audio takes noticeably longer than a pasted transcript.",
      },
      {
        kind: "callout",
        tone: "note",
        title: "Video is not supported",
        text: "Extract or download the audio track first. If audio processing does not finish, Durmah tells you honestly and offers you the faster path: open Edit and paste the transcript text for instant processing.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Your draft is protected",
        text: "Durmah autosaves what you type into the lecture panel. If you close it by accident, reopening offers your unsaved draft back. It also notices if you paste a link into the transcript box, or transcript text into the link box, and offers to move it.",
      },
    ],
    related: [
      "lecture-intelligence",
      "lecture-quality-and-limits",
      "asking-about-a-lecture",
    ],
  },

  {
    slug: "asking-about-a-lecture",
    title: "Asking Durmah about a specific lecture",
    summary:
      "Each lecture has its own conversation, attached to that lecture and still there when you come back next week.",
    category: "lectures",
    audience: "public",
    order: 3,
    readTime: "2 min read",
    tags: ["Lectures", "Durmah"],
    keywords: [
      "ask about lecture",
      "lecture chat",
      "question about a lecture",
      "discuss lecture",
    ],
    blocks: [
      {
        kind: "para",
        text: "Every lecture page carries an Ask Durmah panel. Questions you ask there stay attached to that lecture — close the page, come back a fortnight later, and the thread is intact.",
      },
      {
        kind: "bullets",
        intro: "Questions that work well here:",
        items: [
          "What did the lecturer actually decide about this point?",
          "Explain the distinction between these two cases as she framed it.",
          "Which of these authorities is doing the real work in this argument?",
          "Give me three seminar questions on the second half of this lecture.",
          "I did not follow the part about remoteness — walk me through it using her example.",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Use it the same day",
        text: "The highest-value moment for lecture chat is the afternoon of the lecture, while you can still remember which bit lost you. A question you cannot articulate in week nine is easy to articulate in week two.",
      },
    ],
    related: ["lecture-intelligence", "talking-to-durmah", "quiz-me"],
  },

  {
    slug: "lecture-quality-and-limits",
    title: "Lecture quality, statuses and honest limits",
    summary:
      "What the different lecture statuses mean, why Durmah sometimes refuses to analyse a transcript, and the exact file limits.",
    category: "lectures",
    audience: "member",
    order: 4,
    readTime: "3 min read",
    tags: ["Lectures", "Limits", "Member"],
    keywords: [
      "needs attention",
      "transcript too short",
      "lecture failed",
      "lecture stuck",
      "processing",
      "file size limit",
      "how big can my file be",
      "lecture error",
      "transcript quality",
    ],
    blocks: [
      {
        kind: "callout",
        tone: "boundary",
        title: "Why Durmah refuses short transcripts",
        text: "A transcript needs a substantial word count before Durmah will produce academic analysis from it. Below that threshold the lecture is marked Needs attention and you are told why. This is a feature, not a fault: a thin transcript produces confident-sounding invention, and Durmah will not do that. Your transcript is preserved so you can review or replace it.",
      },
      {
        kind: "quote",
        attribution: "What you will see",
        text: "This transcript appears too short or incomplete for reliable academic analysis. Durmah has preserved the transcript for review, but it will not show a full summary, glossary, authorities, or exam-prep analysis from an inadequate transcript.",
      },
      {
        kind: "heading",
        id: "statuses",
        text: "What each status means",
      },
      {
        kind: "table",
        headers: ["Status", "Meaning"],
        rows: [
          ["Uploaded / Queued", "Received, waiting to start."],
          ["Processing / Transcribing", "Audio is being converted to text."],
          ["Summarizing / Verifying", "Analysis is running."],
          ["Ready / Verified", "Complete. Verified means it passed every quality check."],
          [
            "Needs attention",
            "Saved, but the transcript was not adequate for reliable analysis.",
          ],
          ["Failed", "Something went wrong. Retry, or paste the transcript text."],
        ],
      },
      {
        kind: "heading",
        id: "limits",
        text: "File limits",
      },
      {
        kind: "table",
        headers: ["Item", "Limit"],
        rows: [
          ["Transcript file", ".txt, up to 5 MB"],
          ["Audio file", "MP3, M4A, WAV or OGG"],
          [
            "Very long transcripts",
            "Capped before analysis, with a visible warning — nothing is silently dropped",
          ],
          ["Number of lectures", "No per-day or per-month limit"],
        ],
      },
      {
        kind: "heading",
        id: "empty-states",
        text: "When Durmah finds nothing reliable",
      },
      {
        kind: "para",
        text: "Two honest empty states you may meet. Neither means the lecture failed — they mean Durmah would rather say nothing than say something unsupported.",
      },
      {
        kind: "quote",
        text: "No reliable exam signal was detected from this transcript. This may reflect transcript quality or analyser confidence, not necessarily the lecturer's emphasis.",
      },
      {
        kind: "quote",
        text: "Durmah did not find a transcript-grounded case, statute, treaty, article, regulation, constitutional principle, or scholar reference reliable enough to list separately.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "If a lecture seems stuck",
        text: "Audio takes time — give it a few minutes. If it times out, open Edit and paste the transcript text for instant processing. That path almost always works and is faster than retrying the upload.",
      },
    ],
    related: ["uploading-a-lecture", "lecture-intelligence", "getting-help"],
  },

  /* ---------------------------------------------------------------- */
  /* My Internship                                                     */
  /* ---------------------------------------------------------------- */

  {
    slug: "my-internship",
    title: "My Internship: from placement to a supervisor-ready draft",
    summary:
      "A member workspace for real placements: prepare before day one, work each task through a guided six-stage pipeline, log the authorities you find, and export a working draft your supervisor can review.",
    category: "your-workspace",
    audience: "member",
    order: 4,
    readTime: "4 min read",
    tags: ["Workspace", "Internships", "Members"],
    keywords: [
      "internship",
      "placement",
      "vacation scheme",
      "work experience",
      "task workbench",
      "findings log",
      "working draft",
      "firm brief",
      "supervisor",
      "confidentiality",
      "client material",
      "export docx pdf",
    ],
    blocks: [
      {
        kind: "para",
        text: "My Internship supports a real placement from the moment you accept it. You record the placement (firm, dates, practice areas and the legal jurisdiction it works in), and Durmah can build a cited orientation brief about the firm from public sources so you arrive knowing what the firm actually does. You can also upload your internship contract or offer letter; Durmah reads it privately, suggests the placement details for your review, and never treats its own extraction as authoritative.",
      },
      {
        kind: "para",
        text: "Before day one, the readiness checklist and task playbooks cover the common intern tasks: research notes, chronologies, attendance notes, document review and more. Each playbook explains the purpose, the steps, what to ask your supervisor, and how to check quality before handing work in.",
      },
      {
        kind: "heading",
        id: "task-workbench",
        text: "The Task Workbench: six stages per task",
      },
      {
        kind: "steps",
        intro: "Every real task you bring in moves through a guided pipeline. The stages guide you; they never lock you in. You can move back freely, and jumping ahead just asks you to confirm.",
        items: [
          "Capture: add the instruction by upload or paste, set the deadline, and generate a structured summary so you and Durmah share the same understanding.",
          "Clarify: generate the questions worth asking, put them to your supervisor, then record the answers. Everything you generate later uses them.",
          "Plan: get an execution plan, adapt it to how your firm works, and confirm your approach.",
          "Research: follow a research pathway aimed at your placement's jurisdiction and official sources, do the work on the firm's approved services, and log every authority you find in the findings log, exactly as you found it.",
          "Draft: Durmah assembles an internal working draft from your material, your supervisor's answers and your findings. It cites only what you logged, word for word. Anything it cannot support becomes a clearly marked slot for you to verify.",
          "Finalise: work through the quality checklist, then export the draft as a formatted Word or PDF document, labelled as an internal working draft for supervisor review.",
        ],
      },
      {
        kind: "callout",
        tone: "boundary",
        title: "The findings log is yours alone",
        text: "Durmah never adds, fetches, verifies or endorses a source. The log holds what you found on the firm's research services, recorded in your words, and drafts reproduce it exactly. If a citation is wrong, it stays wrong until you correct it: that is the point. Verification is your job and your skill.",
      },
      {
        kind: "heading",
        id: "confidentiality",
        text: "Confidentiality: the firm's rules come first",
      },
      {
        kind: "para",
        text: "Your firm's confidentiality, information-security and AI rules always come first. Where the firm permits it, you can bring task material into the Workbench; Durmah flags likely client-identifying details and references to passwords or credentials as a reminder, without blocking you. Prefer anonymised or redacted material, keep anything the firm has not approved inside the firm's own systems, and remember that responsibility for following the firm's rules stays with you.",
      },
      {
        kind: "heading",
        id: "experience-memory",
        text: "What Durmah remembers afterwards",
      },
      {
        kind: "para",
        text: "With your consent, Durmah keeps a durable memory of a completed internship: the placement details, your task titles and your own reflections. It can then connect that experience to your studies, for example reminding you that a module topic is the same doctrine you researched on placement. It never remembers uploaded documents or task material, and a toggle on the placement turns this memory off at any time, everywhere.",
      },
      {
        kind: "callout",
        tone: "note",
        title: "Want a rehearsal first?",
        text: "The Practice Desk is a fully fictional matter you can work end to end before your placement starts. Nothing in it is real, which makes it the safe place to learn the shape of the work.",
      },
    ],
    related: ["dashboard-tour", "floating-tools", "getting-help"],
  },
];
